import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Drawer, Table, Tag, Descriptions, Statistic, Row, Col, Card, Spin, Alert, Typography, Tooltip, Space, Collapse, Button, Modal, Input, message } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, HolderOutlined, DollarOutlined, ThunderboltOutlined, SettingOutlined, FileTextOutlined, FilePdfOutlined, RobotOutlined, LoadingOutlined, ExclamationCircleOutlined, FlagFilled, SaveOutlined } from '@ant-design/icons';
import ThreeTablesModal from '../SearchResultModal.jsx';
import getUserId from '../../functions/getUserId';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import api from '../../functions/api';
import { callLLM, formatFundamentalFieldsForPrompt, formatTablesDataForPrompt, formatPortfolioPositionForPrompt } from '../../functions/LLM_analysis_functions';
import { fetchBroadPictureText, broadPicturePromptBlock } from '../../functions/fetchBroadPictureText'; // DAS-2554

const { Text } = Typography;
const { Panel } = Collapse;

/**
 * DAS-1779: Select Now Drawer
 * Shows what stocks would be selected TODAY based on a run's configuration.
 * Displays buy/sell/hold recommendations with quantities and values.
 */
const SelectNowDrawer = ({ open, onClose, runId, callAction }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [searchResultModal, setSearchResultModal] = useState({ open: false, ticker: null });
  
  // DAS-1790: Jira ticket creation state
  const [jiraModalOpen, setJiraModalOpen] = useState(false);
  const [jiraDescription, setJiraDescription] = useState('');
  const [jiraLoading, setJiraLoading] = useState(false);
  
  // DAS-1790: PDF download ref and state
  const contentRef = useRef(null);
  const [pdfMode, setPdfMode] = useState(false); // When true, show all rows for PDF capture
  
  // DAS-1834/DAS-1835: LLM Check state
  const [llmCheckLoading, setLlmCheckLoading] = useState({}); // { ticker: true/false }
  const [llmCheckModal, setLlmCheckModal] = useState({ open: false, ticker: null, response: null });
  const [llmCheckResults, setLlmCheckResults] = useState({}); // { ticker: { score: number, fullResponse: string } }
  // DAS-1836: Batch LLM Check state
  const [batchLLMCheckLoading, setBatchLLMCheckLoading] = useState(false);
  // DAS-1836 FIX: Force re-render counter - increment this to force Table to re-render
  const [forceRenderCount, setForceRenderCount] = useState(0);
  
  // DAS-1856: Save Select Now Results state
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveNotes, setSaveNotes] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    if (open && runId) {
      fetchSelectNow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, runId]);

  const fetchSelectNow = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await callAction('select_now', { run_id: runId }, { useLoader: false });
      if (response?.success && response?.data?.success !== false) {
        setData(response.data?.data || response.data);
      } else {
        setError(response?.data?.error || response?.error || 'Failed to calculate selections');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch selection data');
    } finally {
      setLoading(false);
    }
  };

  // DAS-1790: Create Jira Ticket
  const handleCreateJiraTicket = async () => {
    if (!data) {
      message.error('No data to create ticket');
      return;
    }
    
    setJiraLoading(true);
    try {
      const response = await callAction('create_selectnow_jira_ticket', {
        run_id: runId,
        description: jiraDescription,
        select_now_data: data
      }, { useLoader: false });
      
      if (response?.success && response?.data?.key) {
        message.success(`Created Jira ticket: ${response.data.key}`);
        setJiraModalOpen(false);
        setJiraDescription('');
        
        // Open ticket in new tab
        if (response.data.url) {
          window.open(response.data.url, '_blank');
        }
      } else {
        message.error(response?.data?.error || response?.error || 'Failed to create Jira ticket');
      }
    } catch (err) {
      message.error(err.message || 'Failed to create Jira ticket');
    } finally {
      setJiraLoading(false);
    }
  };
  
  // DAS-1856: Save Select Now Result
  const handleSaveSelectNow = async () => {
    if (!data) {
      message.error('No data to save');
      return;
    }
    
    setSaveLoading(true);
    try {
      // Merge LLM check results into recommendations
      const recommendationsWithLLM = (data.recommendations || []).map(rec => ({
        ...rec,
        llmResult: llmCheckResults[rec.ticker] || rec.llmResult || null
      }));
      
      // Build config summary
      const configSummary = {
        factors: data.config?.factor_configs?.map(f => f.factor) || [],
        selection_mode: data.config?.selection_mode || 'combined_score',
        num_stocks: data.config?.num_stocks,
        stock_region: data.config?.stock_region || 'all',
        sector_neutral: data.config?.sector_neutral,
      };
      
      // Universe size info (simple summary)
      const universeSize = data.universe_size ? {
        total: data.universe_size.total,
        us: data.universe_size.us,
        european: data.universe_size.european,
        other: data.universe_size.other,
      } : null;
      
      // Universe breakdown (detailed stages)
      const universeBreakdown = data.universe_breakdown || null;
      
      // Summary stats
      const buyValue = (data.recommendations || []).filter(r => r.trade === 'BUY').reduce((sum, r) => sum + (r.trade_value_eur || 0), 0);
      const sellValue = (data.recommendations || []).filter(r => r.trade === 'SELL').reduce((sum, r) => sum + (r.trade_value_eur || 0), 0);
      const summary = {
        buy_count: (data.recommendations || []).filter(r => r.trade === 'BUY').length,
        sell_count: (data.recommendations || []).filter(r => r.trade === 'SELL').length,
        total_buy_value: buyValue,
        total_sell_value: sellValue,
        net_cash_flow: Math.round(sellValue - buyValue),  // Sell brings cash in, buy spends cash
        llm_checked_count: Object.keys(llmCheckResults).length,
      };
      
      const response = await callAction('save_select_now_result', {
        run_id: runId,
        configuration_id: data.configuration_id || null,
        name: saveName || null,
        as_of_date: data.as_of_date || data.date_validation?.selected_date,
        config_summary: configSummary,
        full_config: data.full_config || data.config,  // Use full_config if available
        regime: data.regime,
        date_validation: data.date_validation,
        universe_size: universeSize,
        universe_breakdown: universeBreakdown,  // Detailed breakdown
        data_quality_flags: data.validation_flags,  // The actual flags per ticker
        has_validation_flags: data.has_validation_flags,
        flagged_ticker_count: data.flagged_ticker_count,
        recommendations: recommendationsWithLLM,
        summary: summary,
        // DAS-2344: Forward emergency-derisk recommendation so the saved
        // record retains the trim panel when reopened from SelectNowResultsTab.
        emergency_derisk_recommendation: data.emergency_derisk_recommendation || null,
        notes: saveNotes || null,
      }, { useLoader: false });
      
      if (response?.success) {
        message.success(`Saved Select Now result${saveName ? ` "${saveName}"` : ''} (ID: ${response.result_id || response.data?.result_id})`);
        setSaveModalOpen(false);
        setSaveName('');
        setSaveNotes('');
      } else {
        message.error(response?.error || response?.data?.error || 'Failed to save Select Now result');
      }
    } catch (err) {
      message.error(err.message || 'Failed to save Select Now result');
    } finally {
      setSaveLoading(false);
    }
  };

  // DAS-1790: Download PDF
  const handleDownloadPDF = async () => {
    if (!contentRef.current || !data) {
      message.error('Content not ready');
      return;
    }
    
    message.loading('Generating PDF... (expanding all rows)', 0);
    
    // Enable PDF mode to show all table rows
    setPdfMode(true);
    
    // Wait for React to re-render with all rows visible
    await new Promise(resolve => setTimeout(resolve, 500));
    
    try {
      const element = contentRef.current;
      
      // Find the Drawer body and temporarily disable scrolling
      const drawerBody = element.closest('.ant-drawer-body');
      const originalOverflow = drawerBody?.style.overflow;
      const originalHeight = drawerBody?.style.height;
      const originalMaxHeight = drawerBody?.style.maxHeight;
      
      if (drawerBody) {
        drawerBody.style.overflow = 'visible';
        drawerBody.style.height = 'auto';
        drawerBody.style.maxHeight = 'none';
      }
      
      element.style.overflow = 'visible';
      element.style.height = 'auto';
      
      // Wait for reflow
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const fullWidth = element.scrollWidth || element.offsetWidth;
      const fullHeight = element.scrollHeight || element.offsetHeight;
      
      console.log('PDF capture dimensions:', fullWidth, 'x', fullHeight, '- Rows:', data.recommendations?.length);
      
      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        width: fullWidth,
        height: fullHeight,
        windowWidth: fullWidth,
        windowHeight: fullHeight,
        scrollX: 0,
        scrollY: 0,
      });
      
      // Restore styles
      if (drawerBody) {
        drawerBody.style.overflow = originalOverflow || '';
        drawerBody.style.height = originalHeight || '';
        drawerBody.style.maxHeight = originalMaxHeight || '';
      }
      element.style.overflow = '';
      element.style.height = '';
      
      console.log('Canvas dimensions:', canvas.width, 'x', canvas.height);
      
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4',
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const titleHeight = 30;
      const usableHeight = pdfHeight - 2 * margin - titleHeight;
      const usableWidth = pdfWidth - 2 * margin;
      
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      const widthRatio = usableWidth / imgWidth;
      const scaledTotalHeight = imgHeight * widthRatio;
      const totalPages = Math.ceil(scaledTotalHeight / usableHeight);
      const sourceHeightPerPage = usableHeight / widthRatio;
      
      console.log('PDF will have', totalPages, 'pages');
      
      for (let page = 0; page < totalPages; page++) {
        if (page > 0) {
          pdf.addPage();
        }
        
        pdf.setFontSize(14);
        pdf.setTextColor(51, 51, 51);
        pdf.text(`Select Now - Run #${runId} (${data?.as_of_date || 'N/A'}) - Page ${page + 1}/${totalPages}`, margin, margin + 12);
        
        const sourceY = page * sourceHeightPerPage;
        const sourceHeight = Math.min(sourceHeightPerPage, imgHeight - sourceY);
        
        if (sourceHeight <= 0) break;
        
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = imgWidth;
        pageCanvas.height = Math.ceil(sourceHeight);
        const ctx = pageCanvas.getContext('2d');
        
        ctx.drawImage(
          canvas,
          0, sourceY,
          imgWidth, sourceHeight,
          0, 0,
          imgWidth, sourceHeight
        );
        
        const pageImgData = pageCanvas.toDataURL('image/png');
        const destHeight = sourceHeight * widthRatio;
        
        pdf.addImage(pageImgData, 'PNG', margin, margin + titleHeight, usableWidth, destHeight);
      }
      
      const filename = `SelectNow_Run${runId}_${data?.as_of_date || 'export'}.pdf`;
      pdf.save(filename);
      message.destroy();
      message.success(`PDF downloaded (${totalPages} pages, ${data.recommendations?.length} rows)`);
    } catch (err) {
      message.destroy();
      message.error('Failed to generate PDF');
      console.error('PDF generation error:', err);
    } finally {
      // Always restore normal mode
      setPdfMode(false);
    }
  };

  const getTradeColor = (trade) => {
    switch (trade) {
      case 'BUY': return 'green';
      case 'SELL': return 'red';
      case 'HOLD': return 'blue';
      case 'RUNNER_UP': return 'orange';
      default: return 'default';
    }
  };

  const getTradeIcon = (trade) => {
    switch (trade) {
      case 'BUY': return <ArrowUpOutlined />;
      case 'SELL': return <ArrowDownOutlined />;
      case 'HOLD': return <HolderOutlined />;
      case 'RUNNER_UP': return <ThunderboltOutlined />;
      default: return null;
    }
  };

  
  const getTrendColor = (trend) => {
    if (!trend) return 'default';
    const t = trend.toLowerCase();
    if (t.includes('improvement') || t.includes('positive')) return 'green';
    if (t.includes('deterioration') || t.includes('negative')) return 'red';
    return 'orange';
  };

  // DAS-1835: Helper to format stock data into structured prompt
  // DAS-1841: Added record parameter to include all record fields
  const formatStockDataForPrompt = (stockData, tablesData, tickerPortfolioData, record = null) => {
    let prompt = '';

    // DAS-1841: Recommendation Context - all fields from the table record
    if (record) {
      prompt += `--- RECOMMENDATION CONTEXT ---\n`;
      prompt += `Ticker: ${record.ticker || 'N/A'}\n`;
      if (record.company_name) prompt += `Company Name: ${record.company_name}\n`;
      if (record.trade) prompt += `Trade Recommendation: ${record.trade}\n`;
      if (record.quantity !== undefined && record.quantity !== null) prompt += `Recommended Quantity: ${record.quantity.toLocaleString()}\n`;
      if (record.current_qty !== undefined && record.current_qty !== null) prompt += `Current Quantity: ${record.current_qty.toLocaleString()}\n`;
      if (record.target_qty !== undefined && record.target_qty !== null) prompt += `Target Quantity: ${record.target_qty.toLocaleString()}\n`;
      if (record.sector) prompt += `Sector: ${record.sector}\n`;
      if (record.composite_score !== undefined && record.composite_score !== null) prompt += `Composite Score: ${record.composite_score.toFixed(3)}\n`;
      if (record.roa_pct !== undefined && record.roa_pct !== null) prompt += `ROA %: ${record.roa_pct.toFixed(2)}%\n`;
      if (record.roa_zscore !== undefined && record.roa_zscore !== null) prompt += `ROA Z-Score: ${record.roa_zscore.toFixed(2)}\n`;
      if (record.pb_ratio !== undefined && record.pb_ratio !== null) prompt += `P/B Ratio: ${record.pb_ratio.toFixed(2)}\n`;
      if (record.pb_zscore !== undefined && record.pb_zscore !== null) prompt += `P/B Z-Score: ${record.pb_zscore.toFixed(2)}\n`;
      
      // Include any other fields that might be in the record
      const otherFields = Object.keys(record).filter(key => 
        !['ticker', 'company_name', 'trade', 'quantity', 'current_qty', 'target_qty', 'sector', 
          'composite_score', 'roa_pct', 'roa_zscore', 'pb_ratio', 'pb_zscore', 'exists_in_dashboard'].includes(key)
      );
      if (otherFields.length > 0) {
        prompt += `\nOther Fields:\n`;
        otherFields.forEach(key => {
          const value = record[key];
          if (value !== undefined && value !== null && value !== '') {
            prompt += `${key}: ${typeof value === 'number' ? value.toFixed(3) : value}\n`;
          }
        });
      }
      prompt += `\n`;
    }

    // Position Context (if portfolio data available)
    if (tickerPortfolioData) {
      prompt += `--- POSITION CONTEXT ---\n${formatPortfolioPositionForPrompt(tickerPortfolioData)}\n\n`;
    }

    // Fundamental Context
    if (stockData?.fields) {
      const fundamentals = formatFundamentalFieldsForPrompt(stockData.fields);
      if (fundamentals) {
        prompt += `--- FUNDAMENTAL CONTEXT ---\n${fundamentals}\n\n`;
      }
    }

    // Other Data Context (tables) - includes DARLING VIEW, FUNDAMENTAL VIEW, TECHNICAL VIEW, etc.
    if (tablesData && tablesData.length > 0) {
      const tables = formatTablesDataForPrompt(tablesData);
      if (tables) {
        prompt += `--- OTHER DATA CONTEXT ---\n${tables}\n\n`;
      }
    }

    return prompt.trim();
  };

  // DAS-1835: Extract score from LLM response
  const extractScoreFromResponse = (response) => {
    if (!response) return null;
    
    // DEBUG: Log first 200 chars of response to help debug extraction
    console.log(`[Score Extract] Response preview: ${response.substring(0, 200)}...`);
    
    // Priority 1: Find "X/10" pattern (most specific and reliable)
    const slashPattern = response.match(/(\d+(?:\.\d+)?)\s*\/\s*10\b/i);
    if (slashPattern) {
      const score = parseFloat(slashPattern[1]);
      console.log(`[Score Extract] Found X/10 pattern: ${score}`);
      if (score >= 0 && score <= 10) {
        return Math.round(score);
      }
    }
    
    // Priority 2: Find "X out of 10" pattern
    const outOfPattern = response.match(/(\d+(?:\.\d+)?)\s+out\s+of\s+10\b/i);
    if (outOfPattern) {
      const score = parseFloat(outOfPattern[1]);
      console.log(`[Score Extract] Found X out of 10 pattern: ${score}`);
      if (score >= 0 && score <= 10) {
        return Math.round(score);
      }
    }
    
    // Priority 3: Find score near keywords like "score", "rating", "buy"
    const keywordPatterns = [
      /(?:score|rating|grade|buy\s+score)[:\s]+(\d+(?:\.\d+)?)/i,
      /(?:is|would\s+be|rates?|scores?)\s+(?:a\s+)?(\d+(?:\.\d+)?)\s*(?:out\s+of\s+10|\/10|on\s+a\s+scale)/i,
      /(\d+(?:\.\d+)?)\s*(?:out\s+of\s+10|\/10|on\s+a\s+scale\s+of\s+0-10)/i
    ];
    
    for (const pattern of keywordPatterns) {
      const match = response.match(pattern);
      if (match) {
        const score = parseFloat(match[1]);
        console.log(`[Score Extract] Found keyword pattern: ${score}`);
        if (score >= 0 && score <= 10) {
          return Math.round(score);
        }
      }
    }
    
    // Priority 4: Look for numbers 1-10 (not 0) near "buy" or "fundamental" keywords
    const contextMatch = response.match(/(?:buy|fundamental|recommend|score|rating).{0,50}?(\d+(?:\.\d+)?)/i);
    if (contextMatch) {
      const score = parseFloat(contextMatch[1]);
      console.log(`[Score Extract] Found context match: ${score}`);
      if (score >= 1 && score <= 10) { // Exclude 0 to avoid false matches
        return Math.round(score);
      }
    }
    
    // Priority 5: Look for standalone numbers like "**7**" or just "7" at end of response
    const boldPattern = response.match(/\*\*(\d+(?:\.\d+)?)\*\*/);
    if (boldPattern) {
      const score = parseFloat(boldPattern[1]);
      console.log(`[Score Extract] Found bold pattern: ${score}`);
      if (score >= 0 && score <= 10) {
        return Math.round(score);
      }
    }
    
    // Priority 6: Last resort - look for "I would give/rate/score" followed by number
    const givePattern = response.match(/(?:I would|I'd)\s+(?:give|rate|score)\s+(?:it|this)?\s*(?:a\s+)?(\d+(?:\.\d+)?)/i);
    if (givePattern) {
      const score = parseFloat(givePattern[1]);
      console.log(`[Score Extract] Found "I would give" pattern: ${score}`);
      if (score >= 0 && score <= 10) {
        return Math.round(score);
      }
    }
    
    console.log(`[Score Extract] No pattern matched!`);
    return null;
  };

  // DAS-1836: Batch LLM Check for all BUY records
  // FIX: Update data.recommendations directly to force Table re-render
  const handleLLMCheckAllBuy = async () => {
    if (!data?.recommendations) {
      message.warning('No recommendations data available');
      return;
    }

    // Filter: BUY records that exist in dashboard and don't have successful LLM results yet
    // A result is only "complete" if it has a valid score (not null)
    const buyRecords = data.recommendations.filter(record => 
      record.trade === 'BUY' && 
      record.exists_in_dashboard !== false &&
      (!llmCheckResults[record.ticker] || llmCheckResults[record.ticker].score === null)
    );

    if (buyRecords.length === 0) {
      message.info('All BUY records already have successful LLM checks');
      return;
    }

    // Clear any failed results (score: null) before retrying
    setLlmCheckResults(prev => {
      const cleaned = { ...prev };
      buyRecords.forEach(record => {
        if (cleaned[record.ticker]?.score === null) {
          delete cleaned[record.ticker];
        }
      });
      return cleaned;
    });

    setBatchLLMCheckLoading(true);
    message.loading({ content: `Processing 0/${buyRecords.length} BUY records...`, key: 'batch-llm', duration: 0 });

    let successCount = 0;
    let errorCount = 0;

    // Process sequentially to avoid overwhelming the API
    for (let i = 0; i < buyRecords.length; i++) {
      const record = buyRecords[i];
      const ticker = record.ticker;
      
      // Update progress message
      message.loading({ content: `Processing ${i + 1}/${buyRecords.length}: ${ticker}...`, key: 'batch-llm', duration: 0 });
      
      try {
        console.log(`[Batch LLM] Processing ${i + 1}/${buyRecords.length}: ${ticker}`);
        
        setLlmCheckLoading(prev => ({ ...prev, [ticker]: true }));
        
        // Fetch stock data
        const stockResponse = await api.get("/stock_data", { params: { symbol: ticker } });
        
        if (stockResponse.status === 404) {
          console.error(`Ticker ${ticker} not found in database`);
          setLlmCheckLoading(prev => ({ ...prev, [ticker]: false }));
          errorCount++;
          continue;
        }

        const stockData = stockResponse.data;
        const tablesData = stockData.tables || [];
        const tickerPortfolioData = null;

        const fundamentalsFields = stockData.ticker_fundamentals?.fields || 
                                   (stockData.ticker_fundamentals && typeof stockData.ticker_fundamentals === 'object' ? 
                                    Object.keys(stockData.ticker_fundamentals).map(key => ({
                                      label: key,
                                      value: stockData.ticker_fundamentals[key],
                                      quarterTrend: stockData.fundamentals_trend_analysis_data?.[key]?.trend
                                    })) : []);

        const dataPrompt = formatStockDataForPrompt(
          { fields: fundamentalsFields },
          tablesData,
          tickerPortfolioData,
          record
        );

        const regimeBlock = broadPicturePromptBlock(await fetchBroadPictureText()); // DAS-2554
      const fullPrompt = `${dataPrompt}${regimeBlock}

You are an investor expert. Considering the current market regime above AND the stock's fundamentals, on a scale of 0-10, is this stock a buy? Explain your conclusion`;

        const response = await callLLM(fullPrompt, 'deepseek-chat');
        
        // Check if response is an error message (API failure)
        if (response && (response.startsWith('❌') || response.startsWith('⚠️'))) {
          console.error(`[Batch LLM] ${ticker} API error: ${response}`);
          setLlmCheckLoading(prev => {
            const { [ticker]: _, ...rest } = prev;
            return rest;
          });
          errorCount++;
          
          // If quota exceeded (429), stop batch to avoid hammering the API
          if (response.includes('429') || response.includes('quota')) {
            message.error('API quota exceeded. Stopping batch processing. Wait and try again later.');
            break;
          }
          continue;
        }
        
        const score = extractScoreFromResponse(response);
        
        console.log(`[Batch LLM] ${ticker} completed: score=${score}`);
        
        const resultObj = { score, fullResponse: response };
        
        // DAS-1836 FIX: Update state and force re-render
        console.log(`[LLM Batch] Updating ${ticker} with score=${resultObj.score}`);
        
        // Update the results state
        setLlmCheckResults(prev => ({ ...prev, [ticker]: resultObj }));
        
        // Clear loading state for this ticker
        setLlmCheckLoading(prev => {
          const { [ticker]: _, ...rest } = prev;
          return rest;
        });
        
        // FORCE RE-RENDER: Increment counter to change Table key and force re-mount
        setForceRenderCount(prev => prev + 1);
        console.log(`[LLM Batch] Force render count incremented for ${ticker}`);
        
        successCount++;
        
        // Small delay between requests to avoid rate limiting
        if (i < buyRecords.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      } catch (err) {
        console.error(`[Batch LLM] Failed for ${ticker}:`, err);
        setLlmCheckLoading(prev => ({ ...prev, [ticker]: false }));
        errorCount++;
        // Continue processing other records
      }
    }

    message.destroy('batch-llm');
    if (errorCount === 0) {
      message.success(`Successfully processed ${successCount} BUY records`);
    } else {
      message.warning(`Processed ${successCount} records, ${errorCount} failed`);
    }

    setBatchLLMCheckLoading(false);
  };

  // DAS-1834/DAS-1835: Enhanced LLM Check handler
  const handleLLMCheck = async (record) => {
    const ticker = record.ticker;
    
    // If we already have a successful result (with valid score), don't re-run
    // Allow re-checking if score is null (extraction failed)
    if (llmCheckResults[ticker] && llmCheckResults[ticker].score !== null) {
      return;
    }
    
    setLlmCheckLoading(prev => ({ ...prev, [ticker]: true }));
    
    try {
      // DAS-1835: Fetch stock data from /stock_data endpoint
      const stockResponse = await api.get("/stock_data", { params: { symbol: ticker } });
      
      if (stockResponse.status === 404) {
        message.error(`Ticker ${ticker} not found in database`);
        setLlmCheckLoading(prev => ({ ...prev, [ticker]: false }));
        return;
      }

      const stockData = stockResponse.data;
      const tablesData = stockData.tables || [];
      const tickerPortfolioData = null; // Could fetch portfolio data if needed

      // Build structured prompt
      // Note: ticker_fundamentals structure may vary - adjust based on actual response structure
      const fundamentalsFields = stockData.ticker_fundamentals?.fields || 
                                 (stockData.ticker_fundamentals && typeof stockData.ticker_fundamentals === 'object' ? 
                                  Object.keys(stockData.ticker_fundamentals).map(key => ({
                                    label: key,
                                    value: stockData.ticker_fundamentals[key],
                                    quarterTrend: stockData.fundamentals_trend_analysis_data?.[key]?.trend
                                  })) : []);

      // DAS-1841: Include all record fields in the prompt context
      const dataPrompt = formatStockDataForPrompt(
        { fields: fundamentalsFields },
        tablesData,
        tickerPortfolioData,
        record // Pass the full record to include all its fields
      );

      // DAS-1835: Add the specific question
      const regimeBlock = broadPicturePromptBlock(await fetchBroadPictureText()); // DAS-2554
      const fullPrompt = `${dataPrompt}${regimeBlock}

You are an investor expert. Considering the current market regime above AND the stock's fundamentals, on a scale of 0-10, is this stock a buy? Explain your conclusion`;

      const response = await callLLM(fullPrompt, 'deepseek-chat');
      
      // Extract score from response
      const score = extractScoreFromResponse(response);
      
      const resultObj = { score, fullResponse: response };
      
      // DAS-1836: Store result in both places for consistency
      // 1. Separate state (for backwards compatibility)
      setLlmCheckResults(prev => {
        // Allow overwriting if previous result had null score (extraction failed)
        if (prev[ticker] && prev[ticker].score !== null) return prev;
        return { ...prev, [ticker]: resultObj };
      });
      
      // Force re-render by incrementing counter
      setForceRenderCount(prev => prev + 1);
    } catch (err) {
      message.error(`LLM check failed for ${ticker}: ${err.message || err}`);
    } finally {
      setLlmCheckLoading(prev => ({ ...prev, [ticker]: false }));
    }
  };

  // Get config weights for score explanation
  const roaWeight = data?.config_summary?.roa_weight || 50;
  const pbWeight = data?.config_summary?.pb_weight || 50;

  // DAS-1840: Helper to calculate position rankings (sector and overall)
  const calculateRankings = useMemo(() => {
    if (!data?.recommendations) return {};

    const rankings = {};
    const columnsToRank = ['roa_pct', 'roa_zscore', 'pb_ratio', 'pb_zscore', 'ranking', 'momentum_pct', 'market_cap'];
    
    // For each column, calculate rankings
    columnsToRank.forEach(columnKey => {
      // Determine sort direction: higher is better for most, but P/B ratio is lower is better
      const higherIsBetter = !['pb_ratio'].includes(columnKey);
      
      // Sort all records by this column value
      const sortedRecords = [...data.recommendations]
        .filter(r => r[columnKey] != null)
        .sort((a, b) => {
          const valA = a[columnKey];
          const valB = b[columnKey];
          return higherIsBetter ? valB - valA : valA - valB;
        });

      // Calculate overall rankings with proper tie handling
      const overallRankings = {};
      sortedRecords.forEach((record, index) => {
        // Handle ties: same rank for same values, next rank skips
        let rank = index + 1;
        if (index > 0 && sortedRecords[index - 1][columnKey] === record[columnKey]) {
          // Use the same rank as the previous record
          rank = overallRankings[sortedRecords[index - 1].ticker];
        }
        overallRankings[record.ticker] = rank;
      });

      // Calculate sector rankings with proper tie handling
      const sectorRankings = {};
      const sectors = [...new Set(data.recommendations.map(r => r.sector).filter(Boolean))];
      
      sectors.forEach(sector => {
        // Sort sector records separately
        const sectorRecords = sortedRecords
          .filter(r => r.sector === sector)
          .sort((a, b) => {
            const valA = a[columnKey];
            const valB = b[columnKey];
            return higherIsBetter ? valB - valA : valA - valB;
          });
        
        sectorRecords.forEach((record, index) => {
          // Handle ties: same rank for same values
          let rank = index + 1;
          if (index > 0 && sectorRecords[index - 1][columnKey] === record[columnKey]) {
            // Use the same rank as the previous record
            rank = sectorRankings[sectorRecords[index - 1].ticker];
          }
          sectorRankings[record.ticker] = rank;
        });
      });

      rankings[columnKey] = {
        overall: overallRankings,
        sector: sectorRankings,
        totalOverall: sortedRecords.length,
        totalBySector: {}
      };

      // Count total in each sector
      sectors.forEach(sector => {
        const sectorCount = data.recommendations.filter(r => 
          r.sector === sector && r[columnKey] != null
        ).length;
        rankings[columnKey].totalBySector[sector] = sectorCount;
      });
    });

    return rankings;
  }, [data?.recommendations]);

  // DAS-1840: Helper to get ranking display for a column
  const getRankingDisplay = (ticker, columnKey, sector) => {
    const rankings = calculateRankings[columnKey];
    if (!rankings) return null;

    const sectorRank = rankings.sector[ticker];
    const overallRank = rankings.overall[ticker];

    if (sectorRank == null || overallRank == null) return null;

    // Format: (sector_rank/overall_rank) e.g., (5/9) = 5th in sector, 9th overall
    return (
      <div style={{ fontSize: '10px', color: '#999', marginTop: 2 }}>
        ({sectorRank}/{overallRank})
      </div>
    );
  };

  // DAS-1846: Helper to calculate deviation percentage
  const calculateDeviation = (baseValue, dbValue) => {
    if (baseValue == null || dbValue == null || dbValue === 0) return null;
    return Math.abs((baseValue - dbValue) / dbValue) * 100;
  };
  
  // DAS-1836: Columns array - NOT memoized to ensure immediate re-render on state changes
  const columns = [
    // DAS-1846: Add row numbering column
    {
      title: '#',
      key: 'rowNumber',
      width: 50,
      align: 'center',
      fixed: 'left',
      render: (_, __, index) => (
        <Text type="secondary" style={{ fontWeight: 'bold' }}>{index + 1}</Text>
      ),
    },
    {
      title: 'Ticker / Company',
      dataIndex: 'ticker',
      key: 'ticker',
      width: 180,
      fixed: 'left',
      sorter: (a, b) => a.ticker.localeCompare(b.ticker),
      render: (ticker, record) => {
        const isClickable = record.exists_in_dashboard !== false;
        return (
          <div>
            <Tooltip title={!isClickable ? 'Ticker not in dashboard database' : ''}>
              <span
                style={{
                  cursor: isClickable ? 'pointer' : 'not-allowed',
                  color: isClickable ? '#1890ff' : '#999',
                  fontWeight: 'bold',
                  textDecoration: isClickable ? 'underline' : 'none',
                  opacity: isClickable ? 1 : 0.7,
                }}
                onClick={isClickable ? () => setSearchResultModal({ open: true, ticker: ticker }) : undefined}
              >
                {ticker}
              </span>
            </Tooltip>
            {record.company_name && record.company_name !== ticker && (
              <div style={{ fontSize: '11px', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '160px' }}>
                {record.company_name}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Trade',
      dataIndex: 'trade',
      key: 'trade',
      width: 100,
      filters: [
        { text: 'BUY', value: 'BUY' },
        { text: 'SELL', value: 'SELL' },
        { text: 'HOLD', value: 'HOLD' },
        { text: 'RUNNER_UP', value: 'RUNNER_UP' },
      ],
      onFilter: (value, record) => record.trade === value,
      render: (trade) => (
        <Tag color={getTradeColor(trade)} icon={getTradeIcon(trade)}>
          {trade === 'RUNNER_UP' ? 'NEXT' : trade}
        </Tag>
      ),
    },
    {
      title: (
        <Space direction="vertical" size={0} style={{ width: '100%' }}>
          <span>LLM Check</span>
          <Button
            type="link"
            size="small"
            icon={<RobotOutlined />}
            onClick={handleLLMCheckAllBuy}
            disabled={batchLLMCheckLoading || !data?.recommendations}
            loading={batchLLMCheckLoading}
            style={{ 
              padding: '0 4px',
              height: 'auto',
              fontSize: '11px',
              lineHeight: '1.2'
            }}
          >
            Check all Buy
          </Button>
        </Space>
      ),
      key: 'llm_check',
      width: 120,
      align: 'center',
      render: (_, record) => {
        // DAS-1838: Hide LLM Check if ticker doesn't exist in dashboard (same logic as Ticker/Company column)
        const isClickable = record.exists_in_dashboard !== false;
        if (!isClickable) {
          return <Text type="secondary" style={{ fontSize: '12px' }}>N/A</Text>;
        }
        
        const isLoading = llmCheckLoading[record.ticker];
        // DAS-1836 FIX: Use llmCheckResults state for results
        const result = llmCheckResults[record.ticker];
        
        // DEBUG: Log every render to see what's happening
        console.log(`[LLM Render] ${record.ticker}: result=${!!result}, score=${result?.score}`);
        
        // DAS-1835: Show score if we have a result (even if score extraction failed)
        if (result) {
          // If score is null, extraction failed but we still have a response
          const scoreValue = result.score !== null ? result.score : '?';
          // Color code the score: 0-3 red, 4-6 yellow, 7-10 green, gray for unknown
          let color = '#888'; // gray for unknown
          if (result.score !== null) {
            color = '#cf1322'; // red (default)
            if (result.score >= 7) color = '#52c41a'; // green
            else if (result.score >= 4) color = '#faad14'; // yellow
          }
          
          return (
            <Tooltip 
              title={
                <div style={{ 
                  maxWidth: 600, 
                  maxHeight: 500,
                  overflow: 'auto',
                  padding: '12px',
                  background: '#fff',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  fontSize: '13px',
                  lineHeight: '1.6',
                  color: '#333'
                }}>
                  <div style={{ 
                    fontWeight: 'bold', 
                    marginBottom: '12px', 
                    fontSize: '14px',
                    color: '#1890ff',
                    borderBottom: '1px solid #e8e8e8',
                    paddingBottom: '8px'
                  }}>
                    LLM Analysis - {record.ticker}
                  </div>
                  <div style={{ 
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }}>
                    {result.fullResponse}
                  </div>
                </div>
              }
              placement="left"
              overlayStyle={{ maxWidth: '600px' }}
              overlayInnerStyle={{ 
                padding: 0,
                borderRadius: '4px'
              }}
            >
              <div 
                style={{ 
                  fontSize: '18px', 
                  fontWeight: 'bold', 
                  color: color,
                  cursor: 'help',
                  textAlign: 'center',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s',
                  display: 'inline-block',
                  minWidth: '50px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0,0,0,0.04)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                {scoreValue}/10
              </div>
            </Tooltip>
          );
        }
        
        // Show button if no result yet
        return (
          <Button
            type="primary"
            size="small"
            icon={isLoading ? <LoadingOutlined spin /> : <RobotOutlined />}
            onClick={() => handleLLMCheck(record)}
            disabled={isLoading}
            loading={isLoading}
          >
            {isLoading ? 'Checking...' : 'LLM Check'}
          </Button>
        );
      },
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'right',
      sorter: (a, b) => a.quantity - b.quantity,
      render: (qty, record) => {
        if (record.trade === 'HOLD') return '-';
        return <Text strong>{qty.toLocaleString()}</Text>;
      },
    },
    {
      title: 'Current → Target',
      key: 'qty_change',
      width: 110,
      align: 'center',
      render: (_, record) => {
        const current = record.current_qty || 0;
        const target = record.target_qty || 0;
        const net = target - current;
        
        return (
          <div style={{ textAlign: 'center' }}>
            <Space size="small">
              <Text type="secondary">{current}</Text>
              <Text type="secondary">→</Text>
              <Text strong>{target}</Text>
            </Space>
            {/* DAS-1917: Show net buy/sell underneath */}
            {net !== 0 && record.trade !== 'RUNNER_UP' && (
              <div style={{ fontSize: '11px', marginTop: '2px' }}>
                <Text type={net > 0 ? 'danger' : 'success'} style={{ fontSize: '11px' }}>
                  {net > 0 ? `buy net ${net}` : `sell net ${Math.abs(net)}`}
                </Text>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: (
        <Tooltip title={`Source: ${data?.data_sources?.sector?.database || 'correlation_research'}.${data?.data_sources?.sector?.table || 'Ticker_Metadata'}`}>
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>Sector</span>
        </Tooltip>
      ),
      dataIndex: 'sector',
      key: 'sector',
      width: 130,
      ellipsis: true,
      filters: data?.recommendations
        ? [...new Set(data.recommendations.map(r => r.sector))].map(s => ({ text: s, value: s }))
        : [],
      onFilter: (value, record) => record.sector === value,
    },
    {
      title: (
        <Tooltip title={`Return on Assets - higher is better. Source: ${data?.data_sources?.roa?.database || 'correlation_research'}.${data?.data_sources?.roa?.table || 'Fundamentals_Historical'}${data?.data_sources?.roa?.date ? ` (${data.data_sources.roa.date})` : ''}`}>
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>ROA %</span>
        </Tooltip>
      ),
      dataIndex: 'roa_pct',
      key: 'roa_pct',
      width: 85,
      align: 'right',
      sorter: (a, b) => (a.roa_pct || 0) - (b.roa_pct || 0),
      render: (val, record) => {
        // DAS-1846: Check for deviation > 10% when DB value is available
        const deviation = record.dashboard_roa != null && val != null 
          ? calculateDeviation(val, record.dashboard_roa) 
          : null;
        const showWarning = deviation != null && deviation > 10;
        
        // DAS-1851: Check for validation flags on ROA
        const roaFlags = (record.validation_flags || []).filter(f => f.field === 'roa');
        const hasRoaFlag = roaFlags.length > 0;

        return (
          <div>
            {val != null ? (
              <Space size="small">
                <Text style={{ color: val > 0 ? '#52c41a' : '#cf1322' }}>{val.toFixed(1)}%</Text>
                {/* DAS-1851: Show red flag if validation issue */}
                {hasRoaFlag && (
                  <Tooltip title={
                    <div>
                      <strong>🚩 Data Quality Flag</strong>
                      {roaFlags.map((f, i) => (
                        <div key={i} style={{ marginTop: 4 }}>
                          {f.message}
                          {f.value != null && <span> (value: {f.value})</span>}
                        </div>
                      ))}
                    </div>
                  }>
                    <FlagFilled style={{ color: '#cf1322', fontSize: '14px' }} />
                  </Tooltip>
                )}
                {/* DAS-1846: Show warning if deviation > 10% */}
                {showWarning && !hasRoaFlag && (
                  <Tooltip title={`Deviation from DB value: ${deviation.toFixed(1)}% (DB: ${record.dashboard_roa.toFixed(1)}%)`}>
                    <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '14px' }} />
                  </Tooltip>
                )}
              </Space>
            ) : '-'}
            {/* DAS-1840: Show position rankings */}
            {getRankingDisplay(record.ticker, 'roa_pct', record.sector)}
            {record.dashboard_roa != null && (
              <Tooltip title={`Dashboard value (YF_Data)${record.dashboard_roa_date ? ` - ${record.dashboard_roa_date}` : ''}`}>
                <div style={{ fontSize: '10px', color: '#999', marginTop: 2 }}>
                  DB: {record.dashboard_roa.toFixed(1)}%
                </div>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: (
        <Tooltip title="ROA Z-score: How many standard deviations above/below average. Higher = better ROA vs peers.">
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>ROA Z</span>
        </Tooltip>
      ),
      dataIndex: 'roa_zscore',
      key: 'roa_zscore',
      width: 80,
      align: 'right',
      sorter: (a, b) => (a.roa_zscore || 0) - (b.roa_zscore || 0),
      render: (val, record) => (
        <div>
          {val != null ? (
            <Text style={{ color: val > 0 ? '#52c41a' : '#cf1322', fontWeight: 'bold' }}>{val.toFixed(2)}</Text>
          ) : '-'}
          {/* DAS-1840: Show position rankings */}
          {getRankingDisplay(record.ticker, 'roa_zscore', record.sector)}
          {record.dashboard_roa_zscore != null && (
            <Tooltip title="Z-score calculated from dashboard ROA values">
              <div style={{ fontSize: '10px', color: '#999', marginTop: 2 }}>
                DB: {record.dashboard_roa_zscore.toFixed(2)}
              </div>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: (
        <Tooltip title={`Price-to-Book ratio - lower is better. Source: ${data?.data_sources?.price_to_book?.database || 'correlation_research'}.${data?.data_sources?.price_to_book?.table || 'Fundamentals_Historical'}${data?.data_sources?.price_to_book?.date ? ` (${data.data_sources.price_to_book.date})` : ''}`}>
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>P/B</span>
        </Tooltip>
      ),
      dataIndex: 'pb_ratio',
      key: 'pb_ratio',
      width: 75,
      align: 'right',
      sorter: (a, b) => (a.pb_ratio || 0) - (b.pb_ratio || 0),
      render: (val, record) => {
        // DAS-1846: Check for deviation > 10% when DB value is available
        const deviation = record.dashboard_pb != null && val != null 
          ? calculateDeviation(val, record.dashboard_pb) 
          : null;
        const showWarning = deviation != null && deviation > 10;
        
        // DAS-1851: Check for validation flags on P/B
        const pbFlags = (record.validation_flags || []).filter(f => f.field === 'priceToBook');
        const hasPbFlag = pbFlags.length > 0;

        return (
          <div>
            {val != null ? (
              <Space size="small">
                <Text>{val.toFixed(2)}</Text>
                {/* DAS-1851: Show red flag if validation issue */}
                {hasPbFlag && (
                  <Tooltip title={
                    <div>
                      <strong>🚩 Data Quality Flag</strong>
                      {pbFlags.map((f, i) => (
                        <div key={i} style={{ marginTop: 4 }}>
                          {f.message}
                          {f.value != null && <span> (value: {f.value})</span>}
                        </div>
                      ))}
                    </div>
                  }>
                    <FlagFilled style={{ color: '#cf1322', fontSize: '14px' }} />
                  </Tooltip>
                )}
                {/* DAS-1846: Show warning if deviation > 10% */}
                {showWarning && !hasPbFlag && (
                  <Tooltip title={`Deviation from DB value: ${deviation.toFixed(1)}% (DB: ${record.dashboard_pb.toFixed(2)})`}>
                    <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '14px' }} />
                  </Tooltip>
                )}
              </Space>
            ) : '-'}
            {/* DAS-1840: Show position rankings */}
            {getRankingDisplay(record.ticker, 'pb_ratio', record.sector)}
            {record.dashboard_pb != null && (
              <Tooltip title={`Dashboard value (YF_Data)${record.dashboard_pb_date ? ` - ${record.dashboard_pb_date}` : ''}`}>
                <div style={{ fontSize: '10px', color: '#999', marginTop: 2 }}>
                  DB: {record.dashboard_pb.toFixed(2)}
                </div>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: (
        <Tooltip title="P/B Z-score: How many std devs above/below average. SUBTRACTED from score (lower P/B = better).">
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>P/B Z</span>
        </Tooltip>
      ),
      dataIndex: 'pb_zscore',
      key: 'pb_zscore',
      width: 80,
      align: 'right',
      sorter: (a, b) => (a.pb_zscore || 0) - (b.pb_zscore || 0),
      render: (val, record) => (
        <div>
          {val != null ? (
            <Text style={{ color: val < 0 ? '#52c41a' : '#cf1322', fontWeight: 'bold' }}>{val.toFixed(2)}</Text>
          ) : '-'}
          {/* DAS-1840: Show position rankings */}
          {getRankingDisplay(record.ticker, 'pb_zscore', record.sector)}
          {record.dashboard_pb_zscore != null && (
            <Tooltip title="Z-score calculated from dashboard P/B values">
              <div style={{ fontSize: '10px', color: '#999', marginTop: 2 }}>
                DB: {record.dashboard_pb_zscore.toFixed(2)}
              </div>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: (
        <Tooltip 
          title={`Composite Score = (${roaWeight/100} × ROA_Z) − (${pbWeight/100} × P/B_Z). Higher score = better stock. Stocks are ranked by this score and top performers are selected.`}
          placement="topRight"
        >
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999', fontWeight: 'bold' }}>Score</span>
        </Tooltip>
      ),
      dataIndex: 'ranking',
      key: 'ranking',
      width: 85,
      align: 'right',
      sorter: (a, b) => (a.ranking || 0) - (b.ranking || 0),
      defaultSortOrder: 'descend',
      render: (val, record) => {
        const roaZ = record.roa_zscore;
        const pbZ = record.pb_zscore;
        const calcStr = roaZ != null && pbZ != null 
          ? `(${roaWeight/100} × ${roaZ.toFixed(2)}) − (${pbWeight/100} × ${pbZ.toFixed(2)}) = ${val?.toFixed(3) || 'N/A'}`
          : 'N/A';
        return (
          <div>
            {val != null ? (
              <Tooltip title={calcStr}>
                <Text strong style={{ color: val > 0 ? '#52c41a' : '#cf1322' }}>{val.toFixed(3)}</Text>
              </Tooltip>
            ) : '-'}
            {/* DAS-1840: Show position rankings */}
            {getRankingDisplay(record.ticker, 'ranking', record.sector)}
            {record.dashboard_score != null && (
              <Tooltip title="Score calculated from dashboard Z-scores">
                <div style={{ fontSize: '10px', color: '#999', marginTop: 2 }}>
                  DB: {record.dashboard_score.toFixed(3)}
                </div>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: (
        <Tooltip title="6-month price momentum (excluding recent month if configured)">
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>Mom %</span>
        </Tooltip>
      ),
      dataIndex: 'momentum_pct',
      key: 'momentum_pct',
      width: 70,
      align: 'right',
      sorter: (a, b) => (a.momentum_pct || 0) - (b.momentum_pct || 0),
      render: (val, record) => (
        <div>
          {val != null ? (
            <Text style={{ color: val > 0 ? '#52c41a' : '#cf1322' }}>{val.toFixed(1)}%</Text>
          ) : '-'}
          {/* DAS-1840: Show position rankings */}
          {getRankingDisplay(record.ticker, 'momentum_pct', record.sector)}
        </div>
      ),
    },
    {
      title: (
        <Tooltip title={`Market capitalization. Source: ${data?.data_sources?.market_cap?.database || 'correlation_research'}.${data?.data_sources?.market_cap?.table || 'Ticker_Metadata'}`}>
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>Mkt Cap</span>
        </Tooltip>
      ),
      dataIndex: 'market_cap',
      key: 'market_cap',
      width: 90,
      align: 'right',
      render: (val, record) => (
        <div>
          {val || '-'}
          {/* DAS-1840: Show position rankings */}
          {getRankingDisplay(record.ticker, 'market_cap', record.sector)}
          {record.yf_market_cap && (
            <Tooltip title="Live market cap from yfinance">
              <div style={{ fontSize: '10px', color: '#999', marginTop: 2 }}>
                YF: {record.yf_market_cap}
              </div>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Ccy',
      dataIndex: 'currency',
      key: 'currency',
      width: 50,
      align: 'center',
      filters: data?.recommendations
        ? [...new Set(data.recommendations.map(r => r.currency).filter(Boolean))].map(c => ({ text: c, value: c }))
        : [],
      onFilter: (value, record) => record.currency === value,
      render: (val) => val ? <Tag>{val}</Tag> : '-',
    },
    {
      title: (
        <Tooltip title={`Current price in EUR. Source: ${data?.data_sources?.price?.database || 'correlation_research'}.${data?.data_sources?.price?.table || 'performance_historical'}${data?.data_sources?.price?.date ? ` (${data.data_sources.price.date})` : ''}`}>
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>Price (€)</span>
        </Tooltip>
      ),
      dataIndex: 'price_eur',
      key: 'price_eur',
      width: 90,
      align: 'right',
      sorter: (a, b) => (a.price_eur || 0) - (b.price_eur || 0),
      render: (val) => val != null ? `€${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-',
    },
    {
      title: 'Trade Value (€)',
      dataIndex: 'trade_value_eur',
      key: 'trade_value_eur',
      width: 100,
      align: 'right',
      sorter: (a, b) => (a.trade_value_eur || 0) - (b.trade_value_eur || 0),
      render: (val, record) => {
        if (!val || val === 0) return '-';
        const color = record.trade === 'BUY' ? '#cf1322' : record.trade === 'SELL' ? '#52c41a' : undefined;
        return (
          <Text style={{ color }}>
            €{val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Text>
        );
      },
    },
    {
      title: (
        <Tooltip title="Transaction cost for this trade (fixed + % + bps as configured)">
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>Txn Cost</span>
        </Tooltip>
      ),
      dataIndex: 'transaction_cost_eur',
      key: 'transaction_cost_eur',
      width: 80,
      align: 'right',
      sorter: (a, b) => (a.transaction_cost_eur || 0) - (b.transaction_cost_eur || 0),
      render: (val) => val != null && val > 0 ? (
        <Text type="secondary">€{val.toFixed(2)}</Text>
      ) : '-',
    },
    {
      title: (
        <Tooltip title="Fundamental trend analysis: Improvement/Deterioration based on EPS, Revenue, Margins, ROE, FCF trends">
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>Fund Trend</span>
        </Tooltip>
      ),
      dataIndex: 'fundamental_trend',
      key: 'fundamental_trend',
      width: 110,
      align: 'center',
      filters: data?.recommendations
        ? [...new Set(data.recommendations.map(r => r.fundamental_trend).filter(Boolean))].map(t => ({ text: t, value: t }))
        : [],
      onFilter: (value, record) => record.fundamental_trend === value,
      render: (val) => val ? (
        <Tag color={getTrendColor(val)} style={{ fontSize: '10px' }}>
          {val}
        </Tag>
      ) : '-',
    },
    {
      title: (
        <Tooltip title="Trend consensus score (0-100) combining technical, fundamental, sentiment, and institutional signals">
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>Consensus</span>
        </Tooltip>
      ),
      dataIndex: 'consensus_score',
      key: 'consensus_score',
      width: 100,
      align: 'center',
      sorter: (a, b) => (a.consensus_score || 0) - (b.consensus_score || 0),
      render: (score, record) => {
        if (score == null) return '-';
        const direction = record.consensus_direction || '';
        const strength = record.consensus_strength || '';
        let color = '#888';
        if (score >= 60) color = '#52c41a';
        else if (score <= 40) color = '#cf1322';
        else color = '#faad14';
        return (
          <Tooltip title={`Direction: ${direction}, Strength: ${strength}`}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color }}>{score.toFixed(0)}</div>
              <div style={{ fontSize: '9px', color: '#666' }}>{strength || '-'}</div>
            </div>
          </Tooltip>
        );
      },
    },
    // DAS-2212: Zacks column (matches Portfolio View rendering)
    {
      title: 'Zacks',
      key: 'zacks',
      width: 100,
      render: (_, record) => {
        const { zacks_ranking, zacks_growth, zacks_value, zacks_vgm } = record;
        if (!zacks_ranking && !zacks_growth && !zacks_value && !zacks_vgm) {
          return <span style={{ color: '#999' }}>N/A</span>;
        }
        const getRankColor = (rank) => {
          if (!rank) return '#999';
          if (rank <= 2) return '#52c41a';
          if (rank === 3) return '#faad14';
          return '#ff4d4f';
        };
        const getGradeColor = (grade) => {
          if (!grade) return '#999';
          const g = grade.toUpperCase();
          if (g === 'A' || g === 'B') return '#52c41a';
          if (g === 'C') return '#faad14';
          return '#ff4d4f';
        };
        return (
          <Tooltip title={`Rank: ${zacks_ranking || 'N/A'} | Growth: ${zacks_growth || 'N/A'} | Value: ${zacks_value || 'N/A'} | VGM: ${zacks_vgm || 'N/A'}`}>
            <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
              {zacks_ranking && (
                <span style={{
                  backgroundColor: getRankColor(zacks_ranking),
                  color: 'white',
                  padding: '1px 5px',
                  borderRadius: '3px',
                  fontSize: '11px',
                  fontWeight: 'bold'
                }}>
                  {zacks_ranking}
                </span>
              )}
              {zacks_growth && (
                <span style={{ color: getGradeColor(zacks_growth), fontSize: '11px', fontWeight: 'bold' }}>
                  G:{zacks_growth}
                </span>
              )}
              {zacks_value && (
                <span style={{ color: getGradeColor(zacks_value), fontSize: '11px', fontWeight: 'bold' }}>
                  V:{zacks_value}
                </span>
              )}
              {zacks_vgm && (
                <span style={{ color: getGradeColor(zacks_vgm), fontSize: '11px', fontWeight: 'bold' }}>
                  VGM:{zacks_vgm}
                </span>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: (
        <Tooltip title="ESG Sustainability Scores: SA=Sustainalytics Risk Rating (lower is better, 0-40+), MSCI=Letter Rating (AAA-CCC), Refinitiv=Score (0-100, higher is better)">
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>ESG</span>
        </Tooltip>
      ),
      key: 'esg',
      width: 110,
      align: 'center',
      render: (_, record) => {
        const saRisk = record.esg_sa_risk_rating;
        const msci = record.esg_msci_rating;
        const refinitiv = record.esg_refinitiv_score;
        
        // No ESG data at all
        if (saRisk == null && !msci && refinitiv == null) return '-';
        
        // Color coding for SA Risk (lower is better: <10 green, 10-20 yellow, >20 orange, >30 red)
        const getSaColor = (val) => {
          if (val == null) return '#888';
          if (val < 10) return '#52c41a';
          if (val < 20) return '#a0d911';
          if (val < 30) return '#faad14';
          return '#cf1322';
        };
        
        // Color coding for MSCI rating
        const getMsciColor = (rating) => {
          if (!rating) return '#888';
          if (rating === 'AAA' || rating === 'AA') return '#52c41a';
          if (rating === 'A' || rating === 'BBB') return '#a0d911';
          if (rating === 'BB') return '#faad14';
          return '#cf1322';
        };
        
        // Color coding for Refinitiv (higher is better: >70 green, 50-70 yellow, <50 red)
        const getRefColor = (val) => {
          if (val == null) return '#888';
          if (val >= 70) return '#52c41a';
          if (val >= 50) return '#a0d911';
          if (val >= 30) return '#faad14';
          return '#cf1322';
        };
        
        return (
          <Tooltip 
            title={
              <div style={{ fontSize: 11 }}>
                <div><strong>Sustainalytics Risk:</strong> {saRisk != null ? saRisk.toFixed(1) : 'N/A'} (lower is better)</div>
                <div><strong>MSCI Rating:</strong> {msci || 'N/A'}</div>
                <div><strong>Refinitiv Score:</strong> {refinitiv != null ? refinitiv : 'N/A'}/100 (higher is better)</div>
              </div>
            }
          >
            <div style={{ display: 'flex', gap: '2px', justifyContent: 'center', alignItems: 'center' }}>
              {/* Sustainalytics Risk (small box) */}
              <span style={{ 
                fontSize: '9px', 
                padding: '1px 3px', 
                borderRadius: '2px', 
                background: getSaColor(saRisk),
                color: '#fff',
                minWidth: '24px',
                textAlign: 'center'
              }}>
                {saRisk != null ? saRisk.toFixed(0) : '-'}
              </span>
              {/* MSCI Rating */}
              <span style={{ 
                fontSize: '9px', 
                padding: '1px 3px', 
                borderRadius: '2px', 
                background: getMsciColor(msci),
                color: '#fff',
                minWidth: '26px',
                textAlign: 'center'
              }}>
                {msci || '-'}
              </span>
              {/* Refinitiv Score */}
              <span style={{ 
                fontSize: '9px', 
                padding: '1px 3px', 
                borderRadius: '2px', 
                background: getRefColor(refinitiv),
                color: '#fff',
                minWidth: '22px',
                textAlign: 'center'
              }}>
                {refinitiv != null ? refinitiv : '-'}
              </span>
            </div>
          </Tooltip>
        );
      },
    },
  ];
  // NOTE: Removed useMemo wrapper - columns must be recreated on every render
  // to ensure LLM check results appear immediately. The performance impact is negligible.

  return (
    <>
      <Drawer
        title={
          <Space>
            <DollarOutlined />
            <span>Select Now - Run #{runId}</span>
            {data?.as_of_date && (
              <Tooltip 
                title={
                  data?.date_validation?.was_shifted 
                    ? `Data shifted ${data.date_validation.days_shifted} day(s) from ${data.date_validation.original_date} due to insufficient coverage. Coverage: Overall ${data.date_validation.coverage?.overall?.pct || 0}%, US ${data.date_validation.coverage?.us?.pct || 0}%, EU ${data.date_validation.coverage?.eu?.pct || 0}%, Other ${data.date_validation.coverage?.other?.pct || 0}%`
                    : `Using data from ${data.as_of_date}`
                }
              >
                <Tag color={data?.date_validation?.was_shifted ? "orange" : "blue"}>
                  As of {data?.date_validation?.was_shifted ? data.date_validation.selected_date : data.as_of_date}
                  {data?.date_validation?.was_shifted && ` (⚠️ shifted from ${data.date_validation.original_date})`}
                </Tag>
              </Tooltip>
            )}
          </Space>
        }
        placement="right"
        width="90vw"
        open={open}
        onClose={onClose}
        styles={{ body: { padding: '16px' } }}
        extra={
          <Space>
            <Button
              icon={<SaveOutlined />}
              onClick={() => setSaveModalOpen(true)}
              disabled={!data || loading}
              type="primary"
            >
              Save Result
            </Button>
            <Button
              icon={<FileTextOutlined />}
              onClick={() => setJiraModalOpen(true)}
              disabled={!data || loading}
            >
              Create Jira Ticket
            </Button>
            <Button
              icon={<FilePdfOutlined />}
              onClick={handleDownloadPDF}
              disabled={!data || loading}
            >
              Download PDF
            </Button>
          </Space>
        }
      >
        {loading && (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" tip="Calculating selections..." />
          </div>
        )}

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {data && !loading && (
          <div ref={contentRef}>
            {/* DAS-1839: Date Shift Warning */}
            {/* DAS-1847: Fixed - use backend-provided dates directly instead of calculating */}
            {/* DAS-1876: Enhanced with collapsible detailed explanation */}
            {data.date_validation?.was_shifted && (() => {
              const selectedDate = data.date_validation.selected_date;
              const originalDate = data.date_validation.original_date;
              const daysShifted = data.date_validation.days_shifted || 0;
              const rules = data.date_validation.validation_rules;
              const datesChecked = data.date_validation.dates_checked || [];
              const fieldCoverage = data.date_validation.field_coverage;
              
              return (
                <Alert
                  message={
                    <Space>
                      <strong>⚠️ Data Date Shifted</strong>
                      <Text>
                        Using data from <strong>{selectedDate}</strong> instead of <strong>{originalDate}</strong>
                        {' '}({daysShifted} day{daysShifted > 1 ? 's' : ''} ago)
                      </Text>
                    </Space>
                  }
                  description={
                    <div>
                      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                        Requested date ({originalDate}) has insufficient coverage. Coverage on {selectedDate}: 
                        Overall {data.date_validation.coverage?.overall?.pct}% ({data.date_validation.coverage?.overall?.actual}/{data.date_validation.coverage?.overall?.expected}), 
                        US {data.date_validation.coverage?.us?.pct}% ({data.date_validation.coverage?.us?.actual}/{data.date_validation.coverage?.us?.expected}), 
                        EU {data.date_validation.coverage?.eu?.pct}% ({data.date_validation.coverage?.eu?.actual}/{data.date_validation.coverage?.eu?.expected}), 
                        Other {data.date_validation.coverage?.other?.pct}% ({data.date_validation.coverage?.other?.actual}/{data.date_validation.coverage?.other?.expected})
                      </Text>
                      
                      {/* DAS-1876: Collapsible detailed explanation */}
                      <Collapse 
                        ghost 
                        size="small"
                        style={{ marginTop: 8, background: 'transparent' }}
                        items={[{
                          key: 'details',
                          label: <Text type="secondary" style={{ fontSize: 12 }}>📋 Show detailed validation explanation</Text>,
                          children: (
                            <div style={{ fontSize: 12 }}>
                              {/* Validation Rules */}
                              {rules && (
                                <div style={{ marginBottom: 12 }}>
                                  <Text strong style={{ display: 'block', marginBottom: 4 }}>📏 Validation Rules:</Text>
                                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    <li><Text type="secondary">Coverage threshold: <strong>{rules.coverage_threshold_pct}%</strong> minimum for each region</Text></li>
                                    <li><Text type="secondary">Max lookback: <strong>{rules.max_lookback_days} days</strong></Text></li>
                                    <li><Text type="secondary">Required fields: <strong>{rules.required_fields?.join(', ')}</strong></Text></li>
                                    <li><Text type="secondary">Field conditions: {rules.required_field_conditions}</Text></li>
                                    <li><Text type="secondary">Region rule: {rules.region_rule}</Text></li>
                                    <li><Text type="secondary">Data source: <code>{rules.data_source}</code></Text></li>
                                  </ul>
                                </div>
                              )}
                              
                              {/* Field-level coverage issues on selected date */}
                              {fieldCoverage && (
                                <div style={{ marginBottom: 12 }}>
                                  <Text strong style={{ display: 'block', marginBottom: 4 }}>🔍 Field-Level Issues on {selectedDate}:</Text>
                                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    <li>
                                      <Text type="secondary">
                                        Missing ROA: <strong>{fieldCoverage.missing_roa?.total || 0}</strong> tickers
                                        {' '}(US: {fieldCoverage.missing_roa?.us || 0}, EU: {fieldCoverage.missing_roa?.eu || 0}, Other: {fieldCoverage.missing_roa?.other || 0})
                                      </Text>
                                    </li>
                                    <li>
                                      <Text type="secondary">
                                        Missing P/B: <strong>{fieldCoverage.missing_pb?.total || 0}</strong> tickers
                                        {' '}(US: {fieldCoverage.missing_pb?.us || 0}, EU: {fieldCoverage.missing_pb?.eu || 0}, Other: {fieldCoverage.missing_pb?.other || 0})
                                      </Text>
                                    </li>
                                  </ul>
                                </div>
                              )}
                              
                              {/* Dates checked log */}
                              {datesChecked.length > 0 && (
                                <div>
                                  <Text strong style={{ display: 'block', marginBottom: 4 }}>📅 Dates Checked ({datesChecked.length}):</Text>
                                  <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 4, padding: 8 }}>
                                    {datesChecked.map((dateEntry, idx) => (
                                      <div key={idx} style={{ 
                                        marginBottom: 6, 
                                        padding: '4px 8px', 
                                        background: dateEntry.status === 'selected' ? '#f6ffed' : dateEntry.status === 'no_data' ? '#fff2e8' : '#fff7e6',
                                        borderRadius: 4,
                                        borderLeft: `3px solid ${dateEntry.status === 'selected' ? '#52c41a' : dateEntry.status === 'no_data' ? '#fa8c16' : '#faad14'}`,
                                      }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <Text strong>{dateEntry.date}</Text>
                                          <Tag 
                                            size="small" 
                                            color={dateEntry.status === 'selected' ? 'success' : dateEntry.status === 'no_data' ? 'warning' : 'orange'}
                                          >
                                            {dateEntry.status === 'selected' ? '✓ Selected' : dateEntry.status === 'no_data' ? 'No Data' : 'Insufficient'}
                                          </Tag>
                                        </div>
                                        <Text type="secondary" style={{ fontSize: 11 }}>{dateEntry.reason}</Text>
                                        {dateEntry.coverage && (
                                          <div style={{ fontSize: 10, marginTop: 2 }}>
                                            <Text type="secondary">
                                              Coverage: Overall {dateEntry.coverage.overall?.pct}%, 
                                              US {dateEntry.coverage.us?.pct}%, 
                                              EU {dateEntry.coverage.eu?.pct}%, 
                                              Other {dateEntry.coverage.other?.pct}%
                                            </Text>
                                          </div>
                                        )}
                                        {dateEntry.failed_regions && (
                                          <div style={{ marginTop: 2 }}>
                                            {dateEntry.failed_regions.map((region, rIdx) => (
                                              <Tag key={rIdx} color="red" style={{ fontSize: 10, margin: '2px 2px 0 0' }}>❌ {region}</Tag>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ),
                        }]}
                      />
                    </div>
                  }
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              );
            })()}
            
            {/* REGIME INDICATOR - At Top */}
            {data.regime?.enabled && (
              <Alert
                message={
                  <Space>
                    <ThunderboltOutlined />
                    <strong>Market Regime:</strong>
                    <Tag color={data.regime?.current === 'BULL' ? 'green' : data.regime?.current === 'BEAR' ? 'red' : 'orange'}>
                      {data.regime?.current || 'NEUTRAL'}
                    </Tag>
                    <Text type="secondary">VIX: {data.regime?.vix || 'N/A'}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      (Bull &lt;{data.full_config?.regime_adjustment?.vix_bull_threshold}, Bear &gt;{data.full_config?.regime_adjustment?.vix_bear_threshold})
                    </Text>
                    {data.full_config?.rebalancing?.roa_weight_adjusted !== data.full_config?.rebalancing?.roa_weight_base && (
                      <Text type="warning">
                        Weights adjusted: ROA {data.full_config?.rebalancing?.roa_weight_adjusted}% / PB {data.full_config?.rebalancing?.pb_weight_adjusted}%
                      </Text>
                    )}
                  </Space>
                }
                type={data.regime?.current === 'BULL' ? 'success' : data.regime?.current === 'BEAR' ? 'error' : 'warning'}
                style={{ marginBottom: 16 }}
              />
            )}

            {/* DAS-1793: Universe Breakdown Info */}
            {data.universe_breakdown && (
              <Alert
                message={
                  <Space wrap>
                    <Text strong>Universe Size:</Text>
                    {data.universe_breakdown.initial && (
                      <Tooltip title="Initial universe from Ticker_Metadata based on region filter">
                        <Tag>Initial: {data.universe_breakdown.initial.total} 
                          ({data.universe_breakdown.initial.us} US, {data.universe_breakdown.initial.eu} EU, {data.universe_breakdown.initial.other} Other)</Tag>
                      </Tooltip>
                    )}
                    {data.universe_breakdown.after_market_cap_filter && (
                      <Tooltip title="After applying minimum market cap filter">
                        <Tag color="blue">After MktCap: {data.universe_breakdown.after_market_cap_filter.total}</Tag>
                      </Tooltip>
                    )}
                    {data.universe_breakdown.after_fundamental_filter && (
                      <Tooltip title="Tickers with valid ROA and P/B data in Fundamentals_Historical">
                        <Tag color="purple">With Fundamentals: {data.universe_breakdown.after_fundamental_filter.total}
                          ({data.universe_breakdown.after_fundamental_filter.us} US, {data.universe_breakdown.after_fundamental_filter.eu} EU, {data.universe_breakdown.after_fundamental_filter.other} Other)</Tag>
                      </Tooltip>
                    )}
                    {data.universe_breakdown.selected && (
                      <Tooltip title={`Top ${100 - (data.full_config?.rebalancing?.selection_percentile || 90)}% by composite score`}>
                        <Tag color="green">Selected: {data.universe_breakdown.selected.total}
                          ({data.universe_breakdown.selected.us} US, {data.universe_breakdown.selected.eu} EU, {data.universe_breakdown.selected.other} Other)</Tag>
                      </Tooltip>
                    )}
                  </Space>
                }
                type="info"
                style={{ marginBottom: 16 }}
                showIcon
              />
            )}

            {/* DAS-1851: Validation Flags Warning Banner */}
            {data.has_validation_flags && data.flagged_ticker_count > 0 && (
              <Alert
                message={
                  <Space>
                    <FlagFilled style={{ color: '#cf1322' }} />
                    <strong>Data Quality Flags</strong>
                    <Text>
                      {data.flagged_ticker_count} ticker{data.flagged_ticker_count > 1 ? 's' : ''} in results have data quality warnings
                    </Text>
                  </Space>
                }
                description={
                  <Text type="secondary">
                    Look for 🚩 flags in the ROA % and P/B columns. Hover over flagged values for details.
                    Flagged tickers: {Object.keys(data.validation_flags || {}).join(', ')}
                  </Text>
                }
                type="error"
                showIcon
                icon={<FlagFilled />}
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Summary Cards */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={4}>
                <Card size="small">
                  <Statistic
                    title="Buy Trades"
                    value={data.summary?.total_buys || 0}
                    prefix={<ArrowUpOutlined style={{ color: '#52c41a' }} />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col span={4}>
                <Card size="small">
                  <Statistic
                    title="Sell Trades"
                    value={data.summary?.total_sells || 0}
                    prefix={<ArrowDownOutlined style={{ color: '#cf1322' }} />}
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Card>
              </Col>
              <Col span={4}>
                <Card size="small">
                  <Statistic
                    title="Holds"
                    value={data.summary?.total_holds || 0}
                    prefix={<HolderOutlined style={{ color: '#1890ff' }} />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={4}>
                <Card size="small">
                  <Tooltip title="Positive = cash inflow (sells exceed buys)">
                    <Statistic
                      title="Net Cash Flow"
                      value={data.summary?.net_cash_flow_eur || 0}
                      precision={0}
                      prefix="€"
                      valueStyle={{ 
                        color: (data.summary?.net_cash_flow_eur || 0) >= 0 ? '#52c41a' : '#cf1322' 
                      }}
                    />
                  </Tooltip>
                </Card>
              </Col>
              <Col span={4}>
                <Card size="small">
                  <Tooltip title="Total transaction costs for all trades">
                    <Statistic
                      title="Transaction Costs"
                      value={data.summary?.total_transaction_costs_eur || 0}
                      precision={2}
                      prefix="€"
                      valueStyle={{ color: '#cf1322' }}
                    />
                  </Tooltip>
                </Card>
              </Col>
              <Col span={4}>
                <Card size="small">
                  <Statistic
                    title="Cash After Trades"
                    value={data.summary?.cash_after_trades_eur || 0}
                    precision={0}
                    prefix="€"
                    valueStyle={{ color: (data.summary?.cash_after_trades_eur || 0) >= 0 ? '#52c41a' : '#cf1322' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* FULL Configuration - Collapsible, Collapsed by Default */}
            <Collapse 
              ghost 
              defaultActiveKey={[]}
              style={{ marginBottom: 16, background: '#fafafa', borderRadius: 4 }}
            >
              <Panel 
                header={
                  <Space>
                    <SettingOutlined />
                    <strong>Full Configuration</strong>
                    <Text type="secondary">(click to expand)</Text>
                  </Space>
                } 
                key="config"
              >
                <Row gutter={16}>
                  <Col span={6}>
                    <Card size="small" title="Rebalancing">
                      <Descriptions size="small" column={1}>
                        <Descriptions.Item label="Frequency">{data.full_config?.rebalancing?.frequency_days} days</Descriptions.Item>
                        <Descriptions.Item label="ROA Weight (base)">{data.full_config?.rebalancing?.roa_weight_base}%</Descriptions.Item>
                        <Descriptions.Item label="P/B Weight (base)">{data.full_config?.rebalancing?.pb_weight_base}%</Descriptions.Item>
                        <Descriptions.Item label="ROA Weight (adj)">{data.full_config?.rebalancing?.roa_weight_adjusted}%</Descriptions.Item>
                        <Descriptions.Item label="P/B Weight (adj)">{data.full_config?.rebalancing?.pb_weight_adjusted}%</Descriptions.Item>
                        <Descriptions.Item label="Selection">Top {100 - (data.full_config?.rebalancing?.selection_percentile || 90)}%</Descriptions.Item>
                        <Descriptions.Item label="Sector Neutral">{data.full_config?.rebalancing?.sector_neutral ? 'Yes' : 'No'}</Descriptions.Item>
                        <Descriptions.Item label="Max Sector">{data.full_config?.rebalancing?.max_sector_exposure_pct}%</Descriptions.Item>
                        <Descriptions.Item label="Z-Score Base">{data.full_config?.rebalancing?.z_score_baseline}</Descriptions.Item>
                        <Descriptions.Item label="Excluded Sectors">
                          {(data.full_config?.rebalancing?.excluded_sectors || []).join(', ') || 'None'}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card size="small" title="Regime Adjustment">
                      <Descriptions size="small" column={1}>
                        <Descriptions.Item label="Enabled">
                          <Tag color={data.full_config?.regime_adjustment?.enabled ? 'green' : 'default'}>
                            {data.full_config?.regime_adjustment?.enabled ? 'Yes' : 'No'}
                          </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Current Regime">
                          <Tag color={data.full_config?.regime_adjustment?.current_regime === 'BULL' ? 'green' : data.full_config?.regime_adjustment?.current_regime === 'BEAR' ? 'red' : 'orange'}>
                            {data.full_config?.regime_adjustment?.current_regime}
                          </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Current VIX">{data.full_config?.regime_adjustment?.current_vix}</Descriptions.Item>
                        <Descriptions.Item label="Bull Threshold">&lt;{data.full_config?.regime_adjustment?.vix_bull_threshold}</Descriptions.Item>
                        <Descriptions.Item label="Bear Threshold">&gt;{data.full_config?.regime_adjustment?.vix_bear_threshold}</Descriptions.Item>
                        <Descriptions.Item label="Bull ROA/PB mult">{data.full_config?.regime_adjustment?.bull_roa_mult} / {data.full_config?.regime_adjustment?.bull_pb_mult}</Descriptions.Item>
                        <Descriptions.Item label="Bear ROA/PB mult">{data.full_config?.regime_adjustment?.bear_roa_mult} / {data.full_config?.regime_adjustment?.bear_pb_mult}</Descriptions.Item>
                        <Descriptions.Item label="Neutral ROA/PB mult">{data.full_config?.regime_adjustment?.neutral_roa_mult} / {data.full_config?.regime_adjustment?.neutral_pb_mult}</Descriptions.Item>
                        {/* DAS-2344 (B1): Surface DAS-2341 fields in the LIVE drawer
                            (mirror of the same block added to SelectNowResultsTab.jsx) */}
                        <Descriptions.Item label="Position mult (Bull/Neu/Bear)">
                          {data.full_config?.regime_adjustment?.bull_position_mult ?? '-'}
                          {' / '}
                          {data.full_config?.regime_adjustment?.neutral_position_mult ?? '-'}
                          {' / '}
                          {data.full_config?.regime_adjustment?.bear_position_mult ?? '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Cash target % (Bull/Neu/Bear)">
                          {data.full_config?.regime_adjustment?.bull_cash_pct ?? '-'}
                          {' / '}
                          {data.full_config?.regime_adjustment?.neutral_cash_pct ?? '-'}
                          {' / '}
                          {data.full_config?.regime_adjustment?.bear_cash_pct ?? '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Emergency de-risk (DAS-2341)">
                          <Tag color={data.full_config?.regime_adjustment?.emergency_derisk_on_bear_transition ? 'red' : 'default'}>
                            {data.full_config?.regime_adjustment?.emergency_derisk_on_bear_transition ? 'Enabled' : 'Disabled'}
                          </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Emergency lockout (days)">
                          {data.full_config?.regime_adjustment?.emergency_lockout_days
                            ?? data.full_config?.regime_adjustment?.hysteresis_days
                            ?? '-'}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card size="small" title="Transaction Costs">
                      <Descriptions size="small" column={1}>
                        <Descriptions.Item label="Buy Fixed">€{data.full_config?.transaction_costs?.buy_fixed_eur}</Descriptions.Item>
                        <Descriptions.Item label="Sell Fixed">€{data.full_config?.transaction_costs?.sell_fixed_eur}</Descriptions.Item>
                        <Descriptions.Item label="Buy %">{data.full_config?.transaction_costs?.buy_pct}%</Descriptions.Item>
                        <Descriptions.Item label="Sell %">{data.full_config?.transaction_costs?.sell_pct}%</Descriptions.Item>
                        <Descriptions.Item label="Buy bps">{data.full_config?.transaction_costs?.buy_bps}</Descriptions.Item>
                        <Descriptions.Item label="Sell bps">{data.full_config?.transaction_costs?.sell_bps}</Descriptions.Item>
                      </Descriptions>
                    </Card>
                    <Card size="small" title="Momentum Filter" style={{ marginTop: 8 }}>
                      <Descriptions size="small" column={1}>
                        <Descriptions.Item label="Enabled">
                          <Tag color={data.full_config?.momentum_filter?.enabled ? 'green' : 'default'}>
                            {data.full_config?.momentum_filter?.enabled ? 'Yes' : 'No'}
                          </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Min Return">{data.full_config?.momentum_filter?.min_return_pct}%</Descriptions.Item>
                        <Descriptions.Item label="Lookback">{data.full_config?.momentum_filter?.lookback_months} months</Descriptions.Item>
                        <Descriptions.Item label="Skip Recent">{data.full_config?.momentum_filter?.skip_recent_month ? 'Yes' : 'No'}</Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card size="small" title="Position Sizing">
                      <Descriptions size="small" column={1}>
                        <Descriptions.Item label="Mode">{data.full_config?.position_sizing?.mode}</Descriptions.Item>
                        {data.full_config?.position_sizing?.mode === 'fixed_amount' && (
                          <Descriptions.Item label="Amount">€{data.full_config?.position_sizing?.amount_eur?.toLocaleString()}</Descriptions.Item>
                        )}
                        {data.full_config?.position_sizing?.mode === 'percent_equity' && (
                          <Descriptions.Item label="Percent">{data.full_config?.position_sizing?.percent}%</Descriptions.Item>
                        )}
                        <Descriptions.Item label="Target Value">€{data.full_config?.position_sizing?.target_value_eur?.toLocaleString()}</Descriptions.Item>
                      </Descriptions>
                    </Card>
                    <Card size="small" title="Filters" style={{ marginTop: 8 }}>
                      <Descriptions size="small" column={1}>
                        <Descriptions.Item label="Stock Region">{data.full_config?.currency_filters?.stock_region}</Descriptions.Item>
                        <Descriptions.Item label="Liquidity Filter">
                          <Tag color={data.full_config?.liquidity_filter?.enabled ? 'green' : 'default'}>
                            {data.full_config?.liquidity_filter?.enabled ? 'Yes' : 'No'}
                          </Tag>
                        </Descriptions.Item>
                        {data.full_config?.liquidity_filter?.enabled && (
                          <Descriptions.Item label="Min Market Cap">€{(data.full_config?.liquidity_filter?.min_market_cap_eur / 1e9)?.toFixed(1)}B</Descriptions.Item>
                        )}
                        <Descriptions.Item label="Allow Negative Cash">{data.full_config?.cash_policy?.allow_negative_cash ? 'Yes' : 'No'}</Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>
                </Row>
                
                {/* Scoring Formula */}
                <div style={{ 
                  marginTop: 12, 
                  padding: '8px 12px', 
                  background: '#e6f7ff', 
                  borderRadius: 4,
                  fontSize: 13,
                  fontFamily: 'monospace'
                }}>
                  <Text strong>Scoring Formula: </Text>
                  <Text code style={{ fontSize: 12 }}>
                    Score = ({data.full_config?.rebalancing?.roa_weight_adjusted/100} × ROA_Z) − ({data.full_config?.rebalancing?.pb_weight_adjusted/100} × P/B_Z)
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Weights adjusted from base ({data.full_config?.rebalancing?.roa_weight_base}%/{data.full_config?.rebalancing?.pb_weight_base}%) 
                    based on {data.full_config?.regime_adjustment?.current_regime} regime (VIX={data.full_config?.regime_adjustment?.current_vix}).
                  </Text>
                </div>
              </Panel>
            </Collapse>

            {/* Selection Warning */}
            {data.selection_warning && (
              <Alert
                message="Selection Warning"
                description={data.selection_warning}
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {/* DAS-2344 (L2): Suggested Emergency Trim — shown when the live regime
                has just transitioned defensively AND the config has emergency-derisk
                enabled. Mirrors the backtest engine's mid-cycle trim recommendation.
                Mirror of the same panel added to SelectNowResultsTab.jsx. */}
            {data.emergency_derisk_recommendation && (
              <Card
                size="small"
                style={{ marginBottom: 16, borderColor: '#cf1322' }}
                title={
                  <Space>
                    <span style={{ color: '#cf1322', fontWeight: 'bold' }}>
                      ⚠️ Suggested Emergency Trim (DAS-2341/2344)
                    </span>
                    <Tag color="red">
                      {data.emergency_derisk_recommendation.transition_from || '?'}
                      {' → '}
                      {data.emergency_derisk_recommendation.transition_to_live || data.emergency_derisk_recommendation.regime_to}
                    </Tag>
                  </Space>
                }
              >
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="The live market regime has shifted to a more defensive state since the last stored regime."
                  description={
                    <span>
                      The backtest engine would trim positions to reach the regime cash target
                      (<strong>{data.emergency_derisk_recommendation.target_cash_pct}%</strong>)
                      between scheduled rebalances. Suggested action: place the sells listed below at Saxo,
                      OR wait for the next scheduled rebalance if you prefer a full rotation.
                    </span>
                  }
                />
                <Descriptions size="small" column={3} style={{ marginBottom: 12 }}>
                  <Descriptions.Item label="Equity (pre-trim)">
                    €{Number(data.emergency_derisk_recommendation.equity_eur_pre).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </Descriptions.Item>
                  <Descriptions.Item label="Cash % (pre-trim)">
                    {data.emergency_derisk_recommendation.cash_pct_pre}%
                  </Descriptions.Item>
                  <Descriptions.Item label="Cash to raise (est.)">
                    €{Number(data.emergency_derisk_recommendation.cash_raised_eur).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </Descriptions.Item>
                  <Descriptions.Item label="Sells">
                    {data.emergency_derisk_recommendation.sells_count}
                  </Descriptions.Item>
                  <Descriptions.Item label="Est. fees">
                    €{Number(data.emergency_derisk_recommendation.fees_eur).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </Descriptions.Item>
                  <Descriptions.Item label="Target regime">
                    {data.emergency_derisk_recommendation.regime_to}
                  </Descriptions.Item>
                </Descriptions>
                <Table
                  size="small"
                  pagination={false}
                  dataSource={(data.emergency_derisk_recommendation.sells || []).map((s, idx) => ({ key: idx, ...s }))}
                  columns={[
                    { title: 'Ticker', dataIndex: 'ticker', key: 'ticker' },
                    { title: 'Sell Qty', dataIndex: 'qty', key: 'qty', align: 'right' },
                    { title: 'Price (EUR)', dataIndex: 'price', key: 'price', align: 'right',
                      render: v => `€${Number(v).toFixed(2)}` },
                    { title: 'Trade value', dataIndex: 'trade_value_eur', key: 'trade_value_eur', align: 'right',
                      render: v => `€${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}` },
                    { title: 'Fee', dataIndex: 'fee', key: 'fee', align: 'right',
                      render: v => `€${Number(v).toFixed(2)}` },
                    { title: 'Net proceeds', dataIndex: 'proceeds', key: 'proceeds', align: 'right',
                      render: v => `€${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}` },
                  ]}
                />
              </Card>
            )}

            {/* Recommendations Table */}
            {/* DAS-1836: Key forces complete re-render when LLM results change */}
            <Table
              key={`table-${forceRenderCount}`}
              rowKey={(record) => `${record.ticker}-${record.trade}`}
              columns={columns}
              dataSource={data.recommendations || []}
              pagination={pdfMode ? false : { 
                pageSize: 20, 
                showSizeChanger: true, 
                showTotal: (total) => `${total} recommendations` 
              }}
              size="small"
              scroll={pdfMode ? undefined : { x: 1650 }}
              summary={(pageData) => {
                const buys = pageData.filter(r => r.trade === 'BUY');
                const sells = pageData.filter(r => r.trade === 'SELL');
                const buyTotal = buys.reduce((sum, r) => sum + (r.trade_value_eur || 0), 0);
                const sellTotal = sells.reduce((sum, r) => sum + (r.trade_value_eur || 0), 0);
                
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row style={{ background: '#fafafa' }}>
                      <Table.Summary.Cell index={0} colSpan={7}>
                        <Text strong>Page Summary</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={7} align="right">
                        <Text type="secondary">
                          Buys: <Text style={{ color: '#cf1322' }}>€{buyTotal.toLocaleString()}</Text>
                          {' | '}
                          Sells: <Text style={{ color: '#52c41a' }}>€{sellTotal.toLocaleString()}</Text>
                        </Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </div>
        )}
      </Drawer>

      {/* DAS-1779: SearchResultModal for full ticker details */}
      {searchResultModal.open && (
        <ThreeTablesModal
          tickerValue={searchResultModal.ticker}
          open={searchResultModal.open}
          onClose={() => setSearchResultModal({ open: false, ticker: null })}
          userId={getUserId()}
        />
      )}

      {/* DAS-1834: LLM Check Modal */}
      <Modal
        title={
          <Space>
            <RobotOutlined />
            <span>LLM Check - {llmCheckModal.ticker}</span>
          </Space>
        }
        open={llmCheckModal.open}
        onCancel={() => setLlmCheckModal({ open: false, ticker: null, response: null })}
        footer={[
          <Button key="close" onClick={() => setLlmCheckModal({ open: false, ticker: null, response: null })}>
            Close
          </Button>
        ]}
        width={800}
      >
        {llmCheckModal.response ? (
          <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
            <Typography.Paragraph>{llmCheckModal.response}</Typography.Paragraph>
          </div>
        ) : (
          <Spin tip="Loading LLM response..." />
        )}
      </Modal>

      {/* DAS-1790: Jira Ticket Creation Modal */}
      <Modal
        title="Create Jira Ticket for Select Now"
        open={jiraModalOpen}
        onCancel={() => {
          setJiraModalOpen(false);
          setJiraDescription('');
        }}
        onOk={handleCreateJiraTicket}
        okText="Create Ticket"
        confirmLoading={jiraLoading}
        okButtonProps={{ disabled: !data }}
      >
        <div style={{ marginBottom: 16 }}>
          <Typography.Text type="secondary">
            This will create a Jira ticket with the full Select Now results and configuration for Run #{runId}.
          </Typography.Text>
        </div>
        <div style={{ marginBottom: 8 }}>
          <Typography.Text strong>Description (optional):</Typography.Text>
        </div>
        <Input.TextArea
          placeholder="Enter a description for this ticket (e.g., 'Testing ROA-heavy strategy for Q1 rebalancing')"
          value={jiraDescription}
          onChange={(e) => setJiraDescription(e.target.value)}
          rows={4}
          maxLength={500}
          showCount
        />
        <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            <strong>Included in ticket:</strong>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: 16 }}>
              <li>Select Now date and run ID</li>
              <li>{data?.recommendations?.length || 0} recommendations summary</li>
              <li>Full configuration JSON</li>
              <li>Complete results JSON</li>
            </ul>
          </Typography.Text>
        </div>
      </Modal>

      {/* DAS-1856: Save Select Now Result Modal */}
      <Modal
        title={
          <Space>
            <SaveOutlined />
            <span>Save Select Now Result</span>
          </Space>
        }
        open={saveModalOpen}
        onCancel={() => {
          setSaveModalOpen(false);
          setSaveName('');
          setSaveNotes('');
        }}
        onOk={handleSaveSelectNow}
        okText="Save"
        confirmLoading={saveLoading}
        okButtonProps={{ disabled: !data }}
      >
        <div style={{ marginBottom: 16 }}>
          <Typography.Text type="secondary">
            Save this Select Now result (Run #{runId}) for later review. 
            {Object.keys(llmCheckResults).length > 0 && (
              <span style={{ color: '#52c41a' }}>
                {' '}Including {Object.keys(llmCheckResults).length} LLM check results.
              </span>
            )}
          </Typography.Text>
        </div>
        
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>
            <Typography.Text strong>Name (optional):</Typography.Text>
          </div>
          <Input
            placeholder="e.g., 'ROA+P/B Top 15 - Jan 2026'"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            maxLength={255}
          />
        </div>
        
        <div style={{ marginBottom: 8 }}>
          <Typography.Text strong>Notes (optional):</Typography.Text>
        </div>
        <Input.TextArea
          placeholder="Add any notes about this selection (e.g., 'Tested with sector-neutral mode')"
          value={saveNotes}
          onChange={(e) => setSaveNotes(e.target.value)}
          rows={3}
          maxLength={500}
          showCount
        />
        
        <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            <strong>Data being saved:</strong>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: 16 }}>
              <li>As of date: {data?.as_of_date || data?.date_validation?.selected_date || 'N/A'}</li>
              <li>{data?.recommendations?.length || 0} recommendations with scores</li>
              <li>Configuration: {data?.config?.factor_configs?.map(f => f.factor).join(', ') || 'N/A'}</li>
              <li>Market regime: {data?.regime?.current || data?.regime?.label || 'N/A'} (VIX: {data?.regime?.vix?.toFixed(1) || 'N/A'})</li>
              <li>LLM check results: {Object.keys(llmCheckResults).length}</li>
            </ul>
          </Typography.Text>
        </div>
      </Modal>
    </>
  );
};

export default SelectNowDrawer;

